// Talks to the hub's song library API (hub/library.ts) with the control key.

import type { LibraryListing, SavedAnalysis } from "../shared/library.ts";

export class LibraryClient {
  private readonly settings: () => { hub: string; controlKey: string };

  constructor(settings: () => { hub: string; controlKey: string }) {
    this.settings = settings;
  }

  private async request(path: string, init: RequestInit = {}): Promise<Response> {
    const { hub, controlKey } = this.settings();
    // The hub's WebSocket URL doubles as its HTTP address.
    const base = hub.replace(/^ws(s?):\/\//, "http$1://").replace(/\/+$/, "");
    // Street hubs: the admin's session cookie. Development hubs: the room key.
    const res = await fetch(base + path, {
      ...init,
      credentials: "same-origin",
      headers: { ...(init.headers ?? {}), ...(controlKey ? { authorization: `Bearer ${controlKey}` } : {}) },
    });
    if (!res.ok) {
      const message = await res
        .json()
        .then((b: { error?: string }) => b.error)
        .catch(() => undefined);
      throw new LibraryError(res.status, message ?? `HTTP ${res.status}`);
    }
    return res;
  }

  async list(): Promise<LibraryListing[]> {
    return (await (await this.request("/api/library")).json()).songs;
  }

  async analysis(id: string): Promise<SavedAnalysis> {
    return (await this.request(`/api/songs/${id}/analysis`)).json();
  }

  async audio(id: string): Promise<ArrayBuffer> {
    return (await this.request(`/api/songs/${id}/audio`)).arrayBuffer();
  }

  async saveAnalysis(id: string, analysis: SavedAnalysis): Promise<void> {
    await this.request(`/api/songs/${id}/analysis`, { method: "PUT", body: JSON.stringify(analysis) });
  }

  async setPublic(id: string, isPublic: boolean): Promise<void> {
    await this.request(`/api/songs/${id}/sharing`, { method: "PUT", body: JSON.stringify({ public: isPublic }) });
  }

  async setEnabled(id: string, enabled: boolean): Promise<void> {
    await this.request(`/api/songs/${id}/enabled`, { method: "PUT", body: JSON.stringify({ enabled }) });
  }

  async remove(id: string): Promise<void> {
    await this.request(`/api/songs/${id}`, { method: "DELETE" });
  }

  async rename(id: string, title: string): Promise<void> {
    await this.request(`/api/songs/${id}/title`, { method: "PUT", body: JSON.stringify({ title }) });
  }

  async savePlaylist(ids: string[]): Promise<void> {
    await this.request("/api/playlist", { method: "PUT", body: JSON.stringify({ ids }) });
  }

  async upload(file: File): Promise<LibraryListing> {
    const res = await this.request(`/api/songs?name=${encodeURIComponent(file.name)}`, {
      method: "POST",
      body: file,
    });
    return (await res.json()).song;
  }
}

export class LibraryError extends Error {
  readonly status: number;
  constructor(status: number, message: string) {
    super(message);
    this.status = status;
  }
}
